/**
 * Thermal paper profiles (ESC/POS + HTML print).
 *
 * UI labels "58 mm" / "80 mm" name the *roll*, not the Windows/Chromium form.
 * Hardware / ESC/POS drivers almost always print a narrower ink band, e.g.:
 *   80 mm roll → ~72 mm media/print width (forms like 72×200, 72×172, …)
 *   58 mm roll → ~48 mm printable (384 dots @ 8 dot/mm)
 *
 * Electron `pageSize` and CSS `@page` must use mediaWidthMm so Chromium does not
 * scale an "80 mm" page into a 72 mm form (clipped edges / tiny text / wrong height).
 */

export const THERMAL_RECEIPT_PAPER_80 = {
  /** Marketing / roll diameter (settings label only). */
  paperWidthMm: 80,
  /**
   * Physical media / driver form width reported to Chromium+Electron.
   * Matches common 80 mm POS form width (~72 mm), not the roll face width.
   */
  mediaWidthMm: 72,
  /** Content band width (mm). Same as media on 80 mm class printers. */
  printableWidthMm: 72,
  /** Left/right pad when body is media-wide; 0 when media == printable. */
  sideMarginMm: 0,
  /** Extra inset inside the printable band (driver edge clip). */
  safetyInsetMm: 1,
  charWidth: 48,
  columns: { name: 20, qtyPrice: 14, total: 8 }
};

export const THERMAL_RECEIPT_PAPER_80_AR = {
  ...THERMAL_RECEIPT_PAPER_80,
  charWidth: 40,
  columns: { name: 16, qtyPrice: 12, total: 8 }
};

export const THERMAL_RECEIPT_PAPER_58 = {
  paperWidthMm: 58,
  mediaWidthMm: 48,
  printableWidthMm: 48,
  sideMarginMm: 0,
  safetyInsetMm: 0.75,
  charWidth: 32,
  columns: { name: 12, qtyPrice: 12, total: 8 }
};

export const THERMAL_RECEIPT_PAPER_58_AR = {
  ...THERMAL_RECEIPT_PAPER_58,
  charWidth: 28,
  columns: { name: 10, qtyPrice: 10, total: 8 }
};

export const THERMAL_PAPER_PROFILES = {
  '58mm': THERMAL_RECEIPT_PAPER_58,
  '80mm': THERMAL_RECEIPT_PAPER_80
};

export const DEFAULT_THERMAL_PAPER = '80mm';

export const DEFAULT_THERMAL_RECEIPT_CONFIG = {
  paperWidth: DEFAULT_THERMAL_PAPER,
  currencyCode: 'MRO',
  currencyLabel: 'MRO',
  locale: 'fr-FR',
  language: 'fr',
  title: 'REÇU',
  footerMessage: 'Merci pour votre achat!',
  showLogo: true,
  showNif: true,
  showCashier: true,
  showCustomer: true,
  showLocation: false,
  showVat: false,
  showBarcode: true,
  showQrCode: false,
  showItemCount: true,
  compactAmounts: true,
  useCustomerReceiptNumber: true,
  merchantCopy: false,
  lineChar: '-'
};

const WIDTH_58_ALIASES = new Set(['58mm', '58', 'thermal58', 'thermal_58', 'bon58']);

/** Normalize paperWidth (`80mm` | `58mm` and UI aliases). */
export function resolveThermalPaperWidthKey(paperWidth) {
  const raw = String(paperWidth || '').trim().toLowerCase();
  return WIDTH_58_ALIASES.has(raw) ? '58mm' : DEFAULT_THERMAL_PAPER;
}

export function resolveThermalPaperProfile(paperWidthKey, language = 'fr') {
  const key = resolveThermalPaperWidthKey(paperWidthKey);
  if (language === 'ar') {
    return key === '58mm' ? THERMAL_RECEIPT_PAPER_58_AR : THERMAL_RECEIPT_PAPER_80_AR;
  }
  return THERMAL_PAPER_PROFILES[key] || THERMAL_PAPER_PROFILES[DEFAULT_THERMAL_PAPER];
}

/**
 * Width Chromium/Electron should use for `@page` / `pageSize` (mm).
 * Prefer mediaWidthMm (real form), then printable, then paper.
 */
export function thermalMediaWidthMm(profile) {
  if (!profile || typeof profile !== 'object') {
    return THERMAL_RECEIPT_PAPER_80.mediaWidthMm;
  }
  const media = Number(profile.mediaWidthMm);
  if (Number.isFinite(media) && media > 0) return media;
  const print = Number(profile.printableWidthMm);
  if (Number.isFinite(print) && print > 0) return print;
  const paper = Number(profile.paperWidthMm);
  if (Number.isFinite(paper) && paper > 0) return paper;
  return THERMAL_RECEIPT_PAPER_80.mediaWidthMm;
}

export function resolveThermalReceiptConfig(options = {}) {
  const merged = {
    ...DEFAULT_THERMAL_RECEIPT_CONFIG,
    ...options
  };
  const paperWidth = resolveThermalPaperWidthKey(merged.paperWidth || DEFAULT_THERMAL_PAPER);
  const profile = resolveThermalPaperProfile(paperWidth, merged.language || 'fr');

  let currencyCode = String(merged.currencyCode || 'MRO').toUpperCase();
  if (currencyCode === 'UM') currencyCode = 'MRU';
  if (currencyCode !== 'MRU' && currencyCode !== 'MRO') currencyCode = 'MRO';

  return {
    ...merged,
    paperWidth,
    profile,
    currencyCode,
    currencyLabel:
      merged.currencyLabel || (currencyCode === 'MRU' ? 'MRU' : 'MRO')
  };
}

/** Font size so `charWidth` columns fit the printable band (minus safety inset). */
export function computeThermalFontSizePx(profile, scale = 0.96, language = 'fr') {
  const safety = Number(profile?.safetyInsetMm) || 0;
  const printMm = Math.max(
    20,
    (profile?.printableWidthMm ?? THERMAL_RECEIPT_PAPER_80.printableWidthMm) - safety * 2
  );
  const charWidth = profile?.charWidth ?? THERMAL_RECEIPT_PAPER_80.charWidth;
  const pxPerMm = 96 / 25.4;
  const langScale = language === 'ar' ? 0.92 : scale;
  return Math.max(5, (printMm / charWidth) * pxPerMm * langScale);
}

export const THERMAL_RECEIPT_PRINT_MARGINS = { marginType: 'none' };
